/**
 * SimulateImpactModal — Before/after impact comparison
 * for a proposed config change against the universe of clients.
 * Shows what changed, KPI impact, level distribution, and migrations.
 */

import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { BarChart3, ChevronDown, ChevronRight, ArrowUp, ArrowDown } from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ImpactData {
  before: {
    total_profiles: number;
    eligible_count: number;
    eligible_pct: number;
    by_level: Record<string, number>;
    potential_portfolio_mxn: number;
    avg_score: number;
    avg_ticket_mxn: number;
  };
  after: {
    total_profiles: number;
    eligible_count: number;
    eligible_pct: number;
    by_level: Record<string, number>;
    potential_portfolio_mxn: number;
    avg_score: number;
    avg_ticket_mxn: number;
  };
  delta: {
    eligible_count: number;
    eligible_pct: number;
    potential_portfolio_mxn: number;
    avg_score: number;
    level_migrations: { upgrades: number; downgrades: number; unchanged: number };
    eligibility_changes: { became_eligible: number; lost_eligibility: number; unchanged: number };
  };
  migrations: any[];
}

export interface ConfigChange {
  key: string;
  label: string;
  color: string;
  before: number;
  after: number;
  unit: string;
}

interface SimulateImpactModalProps {
  data: ImpactData;
  configType: string;
  configChanges?: ConfigChange[];
  onApply: () => void;
  onCancel: () => void;
  isApplying: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CONFIG_TYPE_LABELS: Record<string, string> = {
  scores: 'Pesos de Scoring',
  'credit-levels': 'Niveles de Credito',
  'interest-rates': 'Tasas de Interes',
  eligibility: 'Elegibilidad',
  'exposure-limits': 'Limites de Exposicion',
};

const LEVEL_COLORS: Record<string, { text: string; bg: string }> = {
  BRONZE: { text: '#CD7F32', bg: 'rgba(205,127,50,0.12)' },
  SILVER: { text: '#94A3B8', bg: 'rgba(148,163,184,0.12)' },
  GOLD: { text: '#DAA520', bg: 'rgba(218,165,32,0.12)' },
  PLATINUM: { text: '#3B82F6', bg: 'rgba(59,130,246,0.12)' },
};

const LEVELS = ['BRONZE', 'SILVER', 'GOLD', 'PLATINUM'];

// ─── MetricCard ───────────────────────────────────────────────────────────────

function MetricCard({
  label,
  before,
  after,
  delta,
  formatDelta,
}: {
  label: string;
  before: string;
  after: string;
  delta: number;
  formatDelta?: (v: number) => string;
}) {
  const isPos = delta > 0.01;
  const isNeg = delta < -0.01;
  const color = isPos ? '#10B981' : isNeg ? '#EF4444' : '#94A3B8';
  const bg = isPos
    ? 'rgba(16,185,129,0.12)'
    : isNeg
      ? 'rgba(239,68,68,0.12)'
      : 'rgba(148,163,184,0.08)';
  const arrow = isPos ? '▲' : isNeg ? '▼' : '—';
  const formatted = formatDelta
    ? formatDelta(Math.abs(delta))
    : Math.abs(delta).toFixed(1);

  return (
    <div className="rounded-lg border border-border p-3 border-l-[3px] border-l-sky-500">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">
        {label}
      </p>
      <div className="flex items-end justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] text-muted-foreground tabular-nums truncate">
            Antes: {before}
          </p>
          <p className="text-base font-bold text-foreground tabular-nums truncate">
            {after}
          </p>
        </div>
        <span
          className="inline-flex shrink-0 items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums whitespace-nowrap"
          style={{ backgroundColor: bg, color }}
        >
          {arrow} {formatted}
        </span>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SimulateImpactModal({
  data,
  configType,
  configChanges,
  onApply,
  onCancel,
  isApplying,
}: SimulateImpactModalProps) {
  const { before, after, delta } = data;
  const [showMigrations, setShowMigrations] = useState(data.migrations.length <= 10);

  const typeLabel = CONFIG_TYPE_LABELS[configType] ?? configType;
  const changedItems = configChanges?.filter((c) => Math.abs(c.after - c.before) > 0.05);

  return (
    <Modal isOpen onClose={onCancel} title="Simulacion de Impacto" size="lg">
      <div className="space-y-5">
        {/* Context */}
        <div className="flex items-center gap-2">
          <BarChart3 size={14} className="text-sky-400 shrink-0" />
          <span className="text-xs text-muted-foreground">
            Cambio en:{' '}
            <span className="font-semibold text-foreground">{typeLabel}</span>
            {' '}&mdash; {before.total_profiles} perfiles evaluados
          </span>
        </div>

        {/* ─── 1. Config Changes ─────────────────────────────────────────── */}
        {configChanges && configChanges.length > 0 && (
          <div className="rounded-lg border border-border p-3 space-y-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              Cambios realizados
            </p>

            {/* Mini stacked bars: before vs after */}
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-[9px] text-muted-foreground w-10 shrink-0 text-right">
                  Antes
                </span>
                <div className="flex h-3 w-full rounded overflow-hidden">
                  {configChanges.map((c) => (
                    <div
                      key={`b-${c.key}`}
                      className="h-full"
                      style={{
                        width: `${c.before}%`,
                        backgroundColor: `${c.color}40`,
                      }}
                      title={`${c.label}: ${c.before.toFixed(1)}%`}
                    />
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[9px] text-muted-foreground w-10 shrink-0 text-right">
                  Ahora
                </span>
                <div className="flex h-3 w-full rounded overflow-hidden">
                  {configChanges.map((c) => (
                    <div
                      key={`a-${c.key}`}
                      className="h-full"
                      style={{
                        width: `${c.after}%`,
                        backgroundColor: `${c.color}90`,
                      }}
                      title={`${c.label}: ${c.after.toFixed(1)}%`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Change chips — only items that actually changed */}
            {changedItems && changedItems.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {changedItems.map((c) => {
                  const d = c.after - c.before;
                  const arrow = d > 0 ? '▲' : '▼';
                  const deltaColor = d > 0 ? '#10B981' : '#EF4444';
                  return (
                    <span
                      key={c.key}
                      className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-medium"
                      style={{
                        borderColor: `${c.color}30`,
                        backgroundColor: `${c.color}08`,
                        borderLeftWidth: '3px',
                        borderLeftColor: c.color,
                      }}
                    >
                      <span style={{ color: c.color }}>{c.label}</span>
                      <span
                        className="tabular-nums font-bold"
                        style={{ color: deltaColor }}
                      >
                        {arrow}
                        {Math.abs(d).toFixed(1)}
                        {c.unit}
                      </span>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ─── 2. KPI Metric Cards ───────────────────────────────────────── */}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">
            Impacto en metricas clave
          </p>
          <div className="grid grid-cols-2 gap-3">
            <MetricCard
              label="Elegibles"
              before={`${before.eligible_count} (${before.eligible_pct}%)`}
              after={`${after.eligible_count} (${after.eligible_pct}%)`}
              delta={delta.eligible_count}
            />
            <MetricCard
              label="Cartera Potencial"
              before={formatCurrency(before.potential_portfolio_mxn, 'MXN', 'es-MX')}
              after={formatCurrency(after.potential_portfolio_mxn, 'MXN', 'es-MX')}
              delta={delta.potential_portfolio_mxn}
              formatDelta={(v) => formatCurrency(v, 'MXN', 'es-MX')}
            />
            <MetricCard
              label="Score Promedio"
              before={before.avg_score.toFixed(1)}
              after={after.avg_score.toFixed(1)}
              delta={delta.avg_score}
            />
            <MetricCard
              label="Ticket Promedio"
              before={formatCurrency(before.avg_ticket_mxn, 'MXN', 'es-MX')}
              after={formatCurrency(after.avg_ticket_mxn, 'MXN', 'es-MX')}
              delta={after.avg_ticket_mxn - before.avg_ticket_mxn}
              formatDelta={(v) => formatCurrency(v, 'MXN', 'es-MX')}
            />
          </div>
        </div>

        {/* ─── 3. Level Distribution ─────────────────────────────────────── */}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">
            Distribucion por Nivel
          </p>
          <div className="grid grid-cols-4 gap-2">
            {LEVELS.map((level) => {
              const lc = LEVEL_COLORS[level]!;
              const bCount = before.by_level[level] ?? 0;
              const aCount = after.by_level[level] ?? 0;
              const d = aCount - bCount;
              return (
                <div
                  key={level}
                  className="rounded-lg border p-2.5 text-center"
                  style={{ borderColor: `${lc.text}30`, backgroundColor: lc.bg }}
                >
                  <span
                    className="inline-block rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider mb-1"
                    style={{ color: lc.text, backgroundColor: `${lc.text}20` }}
                  >
                    {level}
                  </span>
                  <div className="flex items-center justify-center gap-1.5 text-sm tabular-nums">
                    <span className="text-muted-foreground">{bCount}</span>
                    <span className="text-muted-foreground/50 text-xs">&rarr;</span>
                    <span className="font-bold text-foreground">{aCount}</span>
                  </div>
                  {d !== 0 && (
                    <span
                      className="mt-1 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium tabular-nums"
                      style={{
                        backgroundColor: d > 0 ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                        color: d > 0 ? '#10B981' : '#EF4444',
                      }}
                    >
                      {d > 0 ? <ArrowUp size={9} /> : <ArrowDown size={9} />}
                      {Math.abs(d)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── 4. Migrations + Eligibility ───────────────────────────────── */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-border p-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">
              Migraciones de Nivel
            </p>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <ArrowUp size={12} className="text-green-500" />
                  <span className="text-muted-foreground">Subieron</span>
                </span>
                <span className="font-bold tabular-nums text-foreground">
                  {delta.level_migrations.upgrades}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <ArrowDown size={12} className="text-red-500" />
                  <span className="text-muted-foreground">Bajaron</span>
                </span>
                <span className="font-bold tabular-nums text-foreground">
                  {delta.level_migrations.downgrades}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="pl-[18px] text-muted-foreground">Sin cambio</span>
                <span className="font-medium tabular-nums text-muted-foreground">
                  {delta.level_migrations.unchanged}
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border p-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">
              Cambios de Elegibilidad
            </p>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-green-500/20 text-[8px] text-green-500 font-bold">
                    ✓
                  </span>
                  <span className="text-muted-foreground">Ganaron</span>
                </span>
                <span className="font-bold tabular-nums text-foreground">
                  {delta.eligibility_changes.became_eligible}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-500/20 text-[8px] text-red-500 font-bold">
                    ✗
                  </span>
                  <span className="text-muted-foreground">Perdieron</span>
                </span>
                <span className="font-bold tabular-nums text-foreground">
                  {delta.eligibility_changes.lost_eligibility}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="pl-[20px] text-muted-foreground">Sin cambio</span>
                <span className="font-medium tabular-nums text-muted-foreground">
                  {delta.eligibility_changes.unchanged}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ─── 5. Migration Details (collapsible) ────────────────────────── */}
        {data.migrations.length > 0 && (
          <div className="rounded-lg border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => setShowMigrations((p) => !p)}
              className="flex w-full items-center justify-between px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            >
              <span>
                Detalle de Migraciones ({data.migrations.length})
              </span>
              {showMigrations ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
            </button>
            {showMigrations && (
              <div className="border-t border-border px-3 pb-2">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase text-muted-foreground">
                      <th className="py-1.5 font-medium">Cliente</th>
                      <th className="py-1.5 text-right font-medium">Score</th>
                      <th className="py-1.5 font-medium">Nivel</th>
                      <th className="py-1.5 font-medium">Elegible</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.migrations.slice(0, 20).map((m: any, i: number) => {
                      const lcBefore = LEVEL_COLORS[m.level_before] ?? null;
                      const lcAfter = LEVEL_COLORS[m.level_after] ?? null;
                      return (
                        <tr
                          key={m.directory_id ?? i}
                          className="border-t border-border/30"
                        >
                          <td className="py-1 font-mono text-muted-foreground">
                            {m.directory_id?.slice(0, 8)}...
                          </td>
                          <td className="py-1 text-right tabular-nums">
                            <span className="text-muted-foreground">
                              {m.score_before}
                            </span>
                            <span className="mx-1 text-muted-foreground/50">
                              &rarr;
                            </span>
                            <span className="font-semibold">{m.score_after}</span>
                          </td>
                          <td className="py-1">
                            <span className="inline-flex items-center gap-1">
                              <LevelBadge level={m.level_before} colors={lcBefore} />
                              <span className="text-muted-foreground/50">
                                &rarr;
                              </span>
                              <LevelBadge level={m.level_after} colors={lcAfter} />
                            </span>
                          </td>
                          <td className="py-1">
                            <span className="text-muted-foreground">
                              {m.eligible_before ? '✓' : '✗'}
                            </span>
                            <span className="mx-1 text-muted-foreground/50">
                              &rarr;
                            </span>
                            <span
                              className={
                                m.eligible_after
                                  ? 'font-semibold text-green-500'
                                  : 'font-semibold text-red-500'
                              }
                            >
                              {m.eligible_after ? '✓' : '✗'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {data.migrations.length > 20 && (
                  <p className="mt-1.5 text-[10px] text-muted-foreground">
                    ...y {data.migrations.length - 20} mas
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Footer ────────────────────────────────────────────────────── */}
      <div className="sticky bottom-0 -mx-6 -mb-6 mt-4 flex items-center justify-end gap-2 border-t border-border bg-card px-6 py-3 rounded-b-lg">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={isApplying}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<BarChart3 size={14} />}
          onClick={onApply}
          disabled={isApplying}
          isLoading={isApplying}
        >
          {isApplying ? 'Aplicando...' : 'Aplicar Cambio'}
        </Button>
      </div>
    </Modal>
  );
}

// ─── LevelBadge (inline) ──────────────────────────────────────────────────────

function LevelBadge({
  level,
  colors,
}: {
  level: string;
  colors: { text: string; bg: string } | null;
}) {
  if (!colors) return <span className="text-muted-foreground text-[9px]">{level}</span>;
  return (
    <span
      className="rounded px-1 py-0.5 text-[9px] font-bold"
      style={{ color: colors.text, backgroundColor: colors.bg }}
    >
      {level}
    </span>
  );
}
